import * as THREE from 'three';
import { STAR_RADIUS } from './constants';
import { JetStreamConfig } from './types';
import { JetStream } from './JetStream';
import { NeutronStar } from './NeutronStar';

/**
 * JetStreamSystem coordinates two JetStream instances (north and south)
 * aligned to the magnetic axis of the neutron star.
 *
 * The magnetic axis is computed as the spin axis rotated by the magnetic offset angle
 * around a perpendicular axis. As the star rotates, the magnetic axis co-rotates,
 * and the jet emission directions are recalculated each frame.
 */
export class JetStreamSystem {
  public northJet: JetStream;
  public southJet: JetStream;

  private magneticAxisLocal: THREE.Vector3;
  private magneticOffsetAngle: number; // radians
  private neutronStar: NeutronStar;

  constructor(config: JetStreamConfig, neutronStar: NeutronStar) {
    this.neutronStar = neutronStar;
    this.magneticOffsetAngle = THREE.MathUtils.degToRad(config.magneticOffsetAngle);

    // Compute the local magnetic axis (relative to the star's rest frame)
    this.magneticAxisLocal = new THREE.Vector3();
    this.computeMagneticAxisLocal();

    // Create north (+1) and south (-1) jet streams
    this.northJet = new JetStream(1, config);
    this.southJet = new JetStream(-1, config);
  }

  /**
   * Updates both jets each frame.
   * Recalculates the magnetic axis in world space by applying the star's current
   * rotation quaternion to the local magnetic axis, then updates each jet with
   * the computed emission origin and direction.
   */
  update(deltaTime: number): void {
    // Transform the local magnetic axis to world space using the star's current rotation
    const magneticAxisWorld = this.magneticAxisLocal.clone()
      .applyQuaternion(this.neutronStar.mesh.quaternion)
      .normalize();

    // North jet: emission along +magnetic axis direction
    const northDirection = magneticAxisWorld.clone();
    const northOrigin = northDirection.clone().multiplyScalar(STAR_RADIUS);

    // South jet: emission along -magnetic axis direction
    const southDirection = magneticAxisWorld.clone().negate();
    const southOrigin = southDirection.clone().multiplyScalar(STAR_RADIUS);

    // Update both jets
    this.northJet.update(deltaTime, northOrigin, northDirection);
    this.southJet.update(deltaTime, southOrigin, southDirection);
  }

  /**
   * Sets the magnetic offset angle (offset from spin axis).
   * Recomputes the local magnetic axis.
   */
  setMagneticOffset(degrees: number): void {
    this.magneticOffsetAngle = THREE.MathUtils.degToRad(degrees);
    this.computeMagneticAxisLocal();
  }

  /**
   * Sets the particle count for both jets.
   * @param count - The desired particle count
   * @param maxCount - Optional cap to limit the effective particle count (e.g., for mobile)
   */
  setParticleCount(count: number, maxCount?: number): void {
    const effectiveCount = maxCount !== undefined ? Math.min(count, maxCount) : count;
    this.northJet.setTargetCount(effectiveCount);
    this.southJet.setTargetCount(effectiveCount);
  }

  /**
   * Sets the speed for both jets.
   */
  setSpeed(speed: number): void {
    this.northJet.setSpeed(speed);
    this.southJet.setSpeed(speed);
  }

  /**
   * Sets the accent color for both jets.
   */
  setAccentColor(color: THREE.Color): void {
    this.northJet.setAccentColor(color);
    this.southJet.setAccentColor(color);
  }

  /**
   * Returns the current magnetic axis in local (body) frame (for testing).
   */
  getMagneticAxisLocal(): THREE.Vector3 {
    return this.magneticAxisLocal.clone();
  }

  /**
   * Returns the current magnetic offset angle in degrees (for testing).
   */
  getMagneticOffsetDegrees(): number {
    return THREE.MathUtils.radToDeg(this.magneticOffsetAngle);
  }

  /**
   * Computes the local magnetic axis by rotating the Y-axis (spin axis in body frame)
   * by the magnetic offset angle around the X-axis (a perpendicular axis).
   *
   * When magneticOffsetAngle is 0, the magnetic axis aligns exactly with the spin axis (Y-axis).
   */
  private computeMagneticAxisLocal(): void {
    // In the star's local (body) frame, the spin axis is always Y-axis (0, 1, 0).
    // The magnetic axis is obtained by rotating the Y-axis by the offset angle
    // around the X-axis (a perpendicular axis in the body frame).
    this.magneticAxisLocal.set(0, 1, 0);
    const rotationQuat = new THREE.Quaternion();
    rotationQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), this.magneticOffsetAngle);
    this.magneticAxisLocal.applyQuaternion(rotationQuat);
    this.magneticAxisLocal.normalize();
  }
}
